import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  BookOpen,
  RefreshCw,
  Calendar,
  ShieldCheck,
  ShieldOff,
  Eye,
  EyeOff,
} from "lucide-react";
import { patronTypesApi, type PatronType } from "@/lib/api/patron-types";
import { usePatronTypes, PATRON_TYPES_QUERY_KEY } from "@/hooks/usePatronTypes";
import { cn } from "@/lib/utils";

const PatronTypesSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: allPatronTypes = [], isLoading: loading } = usePatronTypes(true, {
    enabled: isTenantAdmin,
  });
  const patronTypes = includeInactive ? allPatronTypes : allPatronTypes.filter((p) => p.isActive);
  const stats = {
    active: allPatronTypes.filter((p) => p.isActive).length,
    inactive: allPatronTypes.filter((p) => !p.isActive).length,
    total: allPatronTypes.length,
  };

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patronToDelete, setPatronToDelete] = useState<PatronType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (patronType: PatronType) => {
    navigate(`/setup/patron-types/${patronType.id}/edit`);
  };

  const openDeleteDialog = (patronType: PatronType) => {
    setPatronToDelete(patronType);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!patronToDelete) return;

    try {
      setDeleting(true);
      // If viewing inactive items or item is already inactive, permanently delete
      // Otherwise, just deactivate
      const hardDelete = includeInactive || !patronToDelete.isActive;
      await patronTypesApi.delete(patronToDelete.id, hardDelete);

      const actionWord = hardDelete ? "permanently deleted" : "deactivated";
      toast.success(`"${patronToDelete.name}" has been ${actionWord}.`);
      queryClient.invalidateQueries(PATRON_TYPES_QUERY_KEY);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Unable to delete patron type. Please try again."
      );
    } finally {
      setDeleting(false);
      setPatronToDelete(null);
    }
  };

  const handleNewClick = () => {
    navigate("/setup/patron-types/new");
  };

  // Filter patron types by search query
  const filteredPatronTypes = patronTypes.filter(
    (patronType) =>
      patronType.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      patronType.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage patron types.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Patron Types"
          description="Create and manage different types of library members with their own borrowing rules"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Patron Types" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name or description..."
        />

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active Types</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <EyeOff className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive Types</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50">
                <ShieldCheck className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Types</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive types
            </Label>
          </div>
          <Button onClick={handleNewClick} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Patron Type
          </Button>
        </div>

        {/* Patron Types List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading patron types..." />
            </div>
          ) : filteredPatronTypes.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching patron types" : "No patron types yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term or check for typos."
                  : "Create your first patron type to define borrowing rules for different library members."}
              </p>
              {!searchQuery && (
                <Button onClick={handleNewClick} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Patron Type
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredPatronTypes.map((patronType) => (
                <div
                  key={patronType.id}
                  className={cn(
                    "p-5 hover:bg-gray-50 transition-colors",
                    !patronType.isActive && "bg-gray-50/50"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {patronType.name}
                        </h3>
                        <span
                          className={cn(
                            "px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                            patronType.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          )}
                        >
                          {patronType.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {patronType.description && (
                        <p className="mb-3 text-sm text-gray-500 line-clamp-2">
                          {patronType.description}
                        </p>
                      )}

                      {/* Borrowing Rules Summary */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <BookOpen className="w-4 h-4 text-primary" />
                          <span>
                            <strong>{patronType.borrowLimit}</strong> books at once
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <RefreshCw className="w-4 h-4 text-primary" />
                          <span>
                            <strong>{patronType.renewalLimit}</strong> renewals
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Calendar className="w-4 h-4 text-primary" />
                          <span>
                            <strong>{patronType.borrowingPeriodDays}</strong> day loan period
                          </span>
                        </div>
                        {patronType.permissions.length > 0 && (
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <ShieldCheck className="w-4 h-4 text-teal-600" />
                            <span>
                              {patronType.permissions.length} permission
                              {patronType.permissions.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(patronType)}
                        className="gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(patronType)}
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {patronType.isActive ? "Deactivate" : "Delete"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="p-4 mt-6 bg-white border border-gray-200 rounded-lg">
          <h4 className="mb-1 font-medium text-gray-900">What are patron types?</h4>
          <p className="text-sm text-gray-600">
            Patron types let you group library members (like students, postGraduate, part-time
            faculty, or community members) and set different borrowing rules for each group. Each
            type can have its own loan limits, renewal policies, and permissions.
          </p>
        </div>
      </PageContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          patronToDelete?.isActive
            ? `Deactivate "${patronToDelete?.name}"?`
            : `Delete "${patronToDelete?.name}"?`
        }
        description={
          patronToDelete?.isActive ? (
            <>
              <p className="mb-2">
                Deactivating this patron type will prevent new members from being assigned to it.
              </p>
              <p className="font-medium text-amber-600">
                Existing members with this type will not be affected.
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this patron type. This action cannot
                be undone.
              </p>
              <p className="font-medium text-red-600">
                Make sure no members are currently assigned to this type.
              </p>
            </>
          )
        }
        confirmLabel={patronToDelete?.isActive ? "Deactivate" : "Delete Permanently"}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default PatronTypesSettingsPage;
